import { classes } from "polytype";
import semverCompare from "semver-compare";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { config } from "../../config/config.js";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import { App } from "../App";
import { AppUserInput } from "../AppUserInput";
import type { SystemSoftware } from "../SystemSoftware";
import type {
  AppVersionData,
  AppVersionListItemData,
  AppVersionListQueryData,
} from "./types.js";

export class AppVersion extends ReferenceModel {
  public readonly app: App;

  public constructor(id: string, app: App) {
    super(id);
    this.app = app;
  }

  public static ofId(id: string, app: App) {
    return new AppVersion(id, app);
  }

  public static find = provideReact(async (id: string, app: App) => {
    const data = await config.behaviors.appVersion.find(id, app.id);
    if (data) {
      return new AppVersionDetailed(data);
    }
  });

  public static get = provideReact(async (id: string, app: App) => {
    const appVersion = await this.find(id, app);
    assertObjectFound(appVersion, "appVersion", id);
    return appVersion;
  });

  public findDetailed = provideReact(
    () => AppVersion.find(this.id, this.app),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<AppVersionDetailed | undefined>>;

  public getDetailed = provideReact(
    () => AppVersion.get(this.id, this.app),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<AppVersionDetailed>>;

  public static query(app: App, query: AppVersionListQueryData = {}) {
    return new AppVersionListQuery(app, query);
  }

  public listUpdateCandidates = provideReact(async (): Promise<
    AppVersionListItem[]
  > => {
    const data = await config.behaviors.appVersion.listUpdateCandidates(
      this.app.id,
      this.id,
    );

    return data
      .map((d) => new AppVersionListItem(d))
      .sort((a, b) => b.compare(a));
  }, [this.id]);

  public updateAvailable = provideReact(async () => {
    return (await this.listUpdateCandidates()).length > 0;
  }, [this.id]);
}

class AppVersionCommon extends classes(
  DataModel<AppVersionData | AppVersionListItemData>,
  AppVersion,
) {
  public readonly version: string;
  public readonly installationUserInputs: AppUserInput[];
  public readonly installationUserInputSteps: string[];
  public readonly docRootUserEditable: boolean;

  public constructor(data: AppVersionData | AppVersionListItemData) {
    super([data], [data.id, App.ofId(data.appId)]);
    this.version = data.externalVersion;
    this.installationUserInputs =
      data.userInputs

        ?.sort(
          (a, b) => (b.positionMeta?.index ?? 0) - (a.positionMeta?.index ?? 0),
        )
        ?.reverse()
        ?.sort(
          (a, b) =>
            (b.positionMeta?.step === "common" ? 1 : 0) -
            (a.positionMeta?.step === "common" ? 1 : 0),
        )
        .map((u) => new AppUserInput(u)) ?? [];
    this.installationUserInputSteps = Array.from(
      new Set(this.installationUserInputs.map((u) => u.step)).values(),
    );
    this.docRootUserEditable = data.docRootUserEditable;
  }

  public isSystemSoftwareRequired(systemSoftware: SystemSoftware) {
    return this.data.systemSoftwareDependencies?.find(
      (d) => d.systemSoftwareId === systemSoftware.id,
    );
  }

  public compare(other: AppVersionListItem): 0 | 1 | -1 {
    return semverCompare(this.data.internalVersion, other.data.internalVersion);
  }
}

export class AppVersionDetailed extends classes(
  AppVersionCommon,
  DataModel<AppVersionData>,
) {
  public constructor(data: AppVersionData) {
    super([data], [data]);
  }
}

export class AppVersionListItem extends classes(
  AppVersionCommon,
  DataModel<AppVersionListItemData>,
) {
  public constructor(data: AppVersionListItemData) {
    super([data], [data]);
  }
}

export class AppVersionListQuery extends ListQueryModel<AppVersionListQueryData> {
  public readonly app: App;

  public constructor(app: App, query: AppVersionListQueryData = {}) {
    super(query, {
      dependencies: [app.id],
    });
    this.app = app;
  }

  public refine(query: AppVersionListQueryData) {
    return new AppVersionListQuery(this.app, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.appVersion.list(
      this.app.id,
      this.query,
    );

    return new AppVersionList(
      this.app,
      this.query,
      items.reverse().map((d) => new AppVersionListItem(d)),
    );
  }, [this.queryId, "appVersion"]);
}

export class AppVersionList extends classes(
  AppVersionListQuery,
  ListDataModel<AppVersionListItem>,
) {
  public constructor(
    app: App,
    query: AppVersionListQueryData,
    appVersions: AppVersionListItem[],
  ) {
    super([app, query], [appVersions, appVersions.length]);
  }
}
